import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

const LiveVisualEditor = ({ clientId }) => {
  const [content, setContent] = useState({ sections: [] });
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);

  const loadContent = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('site_content')
        .select('*')
        .eq('client_id', clientId)
        .eq('page_slug', 'home')
        .single();

      if (data && data.content) {
        setContent(data.content);
      } else {
        setContent({
          sections: [
            {
              id: '1',
              type: 'hero',
              content: {
                title: 'Your Business Name',
                subtitle: 'Welcome to our amazing website. Click here to edit this text.',
                backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textColor: 'white'
              }
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const autoSave = useCallback(async () => {
    setAutoSaving(true);
    try {
      await supabase
        .from('site_content')
        .upsert({
          client_id: clientId,
          page_slug: 'home',
          content,
          title: 'Home Page',
          is_published: false
        });
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setTimeout(() => setAutoSaving(false), 500);
    }
  }, [clientId, content]);

  // Auto-save every 3 seconds when content changes
  useEffect(() => {
    if (loading) return;
    
    const timer = setTimeout(() => {
      autoSave();
    }, 3000);

    return () => clearTimeout(timer);
  }, [content, loading, autoSave]);

  const updateSectionContent = (sectionId, newContent) => {
    setContent(prev => ({
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, content: { ...section.content, ...newContent } }
          : section
      )
    }));
  };

  const addSection = (type, index = null) => {
    const newSection = {
      id: Date.now().toString(),
      type,
      content: getSectionDefaults(type)
    };
    
    setContent(prev => {
      const newSections = [...prev.sections];
      if (index !== null) {
        newSections.splice(index, 0, newSection);
      } else {
        newSections.push(newSection);
      }
      return { sections: newSections };
    });
    
    setSelectedSection(newSection.id);
  };

  const getSectionDefaults = (type) => {
    switch(type) {
      case 'hero':
        return {
          title: 'New Hero Section',
          subtitle: 'Click to edit this subtitle',
          backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          textColor: 'white',
          padding: '4rem 0'
        };
      case 'text':
        return {
          title: 'New Section',
          body: 'Click here to add your content. You can write about your services, story, or anything else.',
          backgroundColor: 'white',
          textColor: '#333',
          padding: '3rem 0'
        };
      case 'image':
        return {
          title: 'Image Section',
          imageUrl: 'https://via.placeholder.com/800x400',
          alt: 'Placeholder image',
          caption: 'Add your image caption here',
          padding: '2rem 0'
        };
      case 'contact':
        return {
          title: 'Contact Us',
          email: 'hello@example.com',
          phone: '(555) 123-4567',
          address: '123 Main St, City, State 12345',
          backgroundColor: '#f8f9fa',
          padding: '3rem 0'
        };
      default:
        return {};
    }
  };

  const deleteSection = (sectionId) => {
    setContent(prev => ({
      sections: prev.sections.filter(section => section.id !== sectionId)
    }));
    setSelectedSection(null);
  };

  const moveSection = (dragIndex, hoverIndex) => {
    setContent(prev => {
      const newSections = [...prev.sections];
      const draggedItem = newSections[dragIndex];
      newSections.splice(dragIndex, 1);
      newSections.splice(hoverIndex, 0, draggedItem);
      return { sections: newSections };
    });
  };

  if (loading) return <div className="p-8 text-center">Loading editor...</div>;

  return (
    <div className="h-full flex bg-gray-100">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-300 flex flex-col">
        {/* Add Elements */}
        <div className="p-4 border-b">
          <h3 className="font-bold text-lg mb-3 text-gray-800">Add Elements</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => addSection('hero')}
              className="p-3 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 text-sm font-medium text-indigo-800 transition-colors"
            >
              + Hero
            </button>
            <button
              onClick={() => addSection('text')}
              className="p-3 bg-green-50 border border-green-200 rounded hover:bg-green-100 text-sm font-medium text-green-800 transition-colors"
            >
              + Text
            </button>
            <button
              onClick={() => addSection('image')}
              className="p-3 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 text-sm font-medium text-blue-800 transition-colors"
            >
              + Image
            </button>
            <button
              onClick={() => addSection('contact')}
              className="p-3 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 text-sm font-medium text-purple-800 transition-colors"
            >
              + Contact
            </button>
          </div>
        </div>

        {/* Section Properties */}
        {selectedSection && (
          <SectionProperties
            section={content.sections.find(s => s.id === selectedSection)}
            onUpdate={(newContent) => updateSectionContent(selectedSection, newContent)}
            onDelete={() => deleteSection(selectedSection)}
          />
        )}

        {/* Auto-save indicator */}
        <div className="mt-auto p-4 border-t">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${autoSaving ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`}></div>
            <span className="text-xs text-gray-600">
              {autoSaving ? 'Saving...' : 'Saved'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 overflow-y-auto bg-white">
        <div className="min-h-full">
          {content.sections.map((section, index) => (
            <LiveSection
              key={section.id}
              section={section}
              index={index}
              isSelected={selectedSection === section.id}
              onSelect={() => setSelectedSection(section.id)}
              onUpdate={(newContent) => updateSectionContent(section.id, newContent)}
              onMove={moveSection}
              onAddBelow={() => setSelectedSection(null)}
            />
          ))}
          
          {/* Add Section Drops */}
          {content.sections.length === 0 && (
            <div className="h-96 flex items-center justify-center border-2 border-dashed border-gray-300 m-8">
              <div className="text-center">
                <p className="text-gray-500 mb-4">Start building your website</p>
                <button
                  onClick={() => addSection('hero')}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Your First Section
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LiveSection = ({ section, index, isSelected, onSelect, onUpdate, onMove }) => {
  const handleContentChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const sectionStyle = {
    backgroundColor: section.content.backgroundColor || 'white',
    color: section.content.textColor || '#333',
    padding: section.content.padding || '2rem 0',
    background: section.content.backgroundColor?.includes('gradient')
      ? section.content.backgroundColor
      : section.content.backgroundColor,
    position: 'relative'
  };

  return (
    <div
      className={`relative group transition-all duration-200 ${
        isSelected ? 'ring-4 ring-indigo-400 ring-opacity-50' : ''
      }`}
      style={sectionStyle}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Selection Overlay */}
      {isSelected && (
        <div className="absolute inset-0 bg-indigo-500 bg-opacity-10 pointer-events-none"></div>
      )}

      {/* Section Content */}
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {section.type === 'hero' && (
          <div className="text-center py-12">
            <h1
              contentEditable
              suppressContentEditableWarning={true}
              className="text-5xl font-bold mb-6 outline-none focus:bg-white focus:bg-opacity-20 focus:px-2 focus:rounded transition-all"
              style={{ color: section.content.textColor }}
              onBlur={(e) => handleContentChange('title', e.target.textContent)}
            >
              {section.content.title}
            </h1>
            <p
              contentEditable
              suppressContentEditableWarning={true}
              className="text-xl opacity-90 max-w-2xl mx-auto outline-none focus:bg-white focus:bg-opacity-20 focus:px-2 focus:rounded transition-all"
              style={{ color: section.content.textColor }}
              onBlur={(e) => handleContentChange('subtitle', e.target.textContent)}
            >
              {section.content.subtitle}
            </p>
          </div>
        )}

        {section.type === 'text' && (
          <div className="py-8">
            <h2
              contentEditable
              suppressContentEditableWarning={true}
              className="text-3xl font-bold mb-6 outline-none focus:bg-gray-100 focus:px-2 focus:rounded transition-all"
              style={{ color: section.content.textColor }}
              onBlur={(e) => handleContentChange('title', e.target.textContent)}
            >
              {section.content.title}
            </h2>
            <div
              contentEditable
              suppressContentEditableWarning={true}
              className="text-lg leading-relaxed outline-none focus:bg-gray-100 focus:px-2 focus:rounded transition-all"
              style={{ color: section.content.textColor }}
              onBlur={(e) => handleContentChange('body', e.target.innerHTML)}
              dangerouslySetInnerHTML={{ __html: section.content.body }}
            />
          </div>
        )}

        {section.type === 'image' && (
          <div className="py-8 text-center">
            <h2
              contentEditable
              suppressContentEditableWarning={true}
              className="text-3xl font-bold mb-6 outline-none focus:bg-gray-100 focus:px-2 focus:rounded transition-all"
              onBlur={(e) => handleContentChange('title', e.target.textContent)}
            >
              {section.content.title}
            </h2>
            <img
              src={section.content.imageUrl}
              alt={section.content.alt}
              className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
            />
            <p
              contentEditable
              suppressContentEditableWarning={true}
              className="mt-4 text-gray-600 outline-none focus:bg-gray-100 focus:px-2 focus:rounded transition-all"
              onBlur={(e) => handleContentChange('caption', e.target.textContent)}
            >
              {section.content.caption}
            </p>
          </div>
        )}

        {section.type === 'contact' && (
          <div className="py-8">
            <h2
              contentEditable
              suppressContentEditableWarning={true}
              className="text-3xl font-bold mb-6 text-center outline-none focus:bg-gray-100 focus:px-2 focus:rounded transition-all"
              onBlur={(e) => handleContentChange('title', e.target.textContent)}
            >
              {section.content.title}
            </h2>
            <div className="max-w-md mx-auto space-y-4 text-center">
              <div>
                <strong>Email: </strong>
                <span
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="outline-none focus:bg-gray-100 focus:px-1 focus:rounded"
                  onBlur={(e) => handleContentChange('email', e.target.textContent)}
                >
                  {section.content.email}
                </span>
              </div>
              <div>
                <strong>Phone: </strong>
                <span
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="outline-none focus:bg-gray-100 focus:px-1 focus:rounded"
                  onBlur={(e) => handleContentChange('phone', e.target.textContent)}
                >
                  {section.content.phone}
                </span>
              </div>
              <div>
                <strong>Address: </strong>
                <span
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="outline-none focus:bg-gray-100 focus:px-1 focus:rounded"
                  onBlur={(e) => handleContentChange('address', e.target.textContent)}
                >
                  {section.content.address}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hover/Selected Controls */}
      {isSelected && (
        <div className="absolute top-2 right-2 flex space-x-2">
          <button
            className="p-2 bg-white rounded shadow hover:bg-gray-50"
            title="Move Up"
          >
            ↑
          </button>
          <button
            className="p-2 bg-white rounded shadow hover:bg-gray-50"
            title="Move Down"
          >
            ↓
          </button>
          <button
            className="p-2 bg-red-500 text-white rounded shadow hover:bg-red-600"
            title="Delete"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

const SectionProperties = ({ section, onUpdate, onDelete }) => {
  if (!section) return null;

  return (
    <div className="flex-1 p-4 overflow-y-auto">
      <h3 className="font-bold text-lg mb-4 text-gray-800">
        {section.type.charAt(0).toUpperCase() + section.type.slice(1)} Properties
      </h3>
      
      <div className="space-y-4">
        {/* Background Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
          <select
            value={section.content.backgroundColor || 'white'}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            className="w-full p-2 border rounded"
          >
            <option value="white">White</option>
            <option value="#f8f9fa">Light Gray</option>
            <option value="#e3f2fd">Light Blue</option>
            <option value="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">Purple Gradient</option>
            <option value="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">Pink Gradient</option>
            <option value="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">Blue Gradient</option>
          </select>
        </div>

        {/* Text Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
          <select
            value={section.content.textColor || '#333'}
            onChange={(e) => onUpdate({ textColor: e.target.value })}
            className="w-full p-2 border rounded"
          >
            <option value="#333">Dark Gray</option>
            <option value="white">White</option>
            <option value="#000">Black</option>
            <option value="#4a5568">Medium Gray</option>
          </select>
        </div>

        {/* Image URL for image sections */}
        {section.type === 'image' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
            <input
              type="url"
              value={section.content.imageUrl || ''}
              onChange={(e) => onUpdate({ imageUrl: e.target.value })}
              className="w-full p-2 border rounded"
              placeholder="https://example.com/image.jpg"
            />
          </div>
        )}

        {/* Delete Button */}
        <div className="pt-4 border-t">
          <button
            onClick={() => onDelete()}
            className="w-full p-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete Section
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveVisualEditor;
